import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isClient } from 'frontend/helpers/ssr-helper';
import useAvailableDates from '../../hooks/useAvailableDates';
import '../../styles/header.css';
import ErrorSnackbar from '../ErrorSnackbar';
import MonthYearGroupedFullScreenDialog from './MonthYearGroupedFullScreenDialog';
import MonthYearGroupedSelector from './MonthYearGroupedSelector';
import OrigenDestinoFullScreenDialog from './OrigenDestinoFullScreenDialog';
import PasajerosFullScreenDialog from './PasajerosFullScreenDialog';
import PasajerosSelector from './PasajerosSelector';
import './styles.css';

const PackageFilterForm = ({
  origins,
  destinations,
  handleOriginSelect,
  handleDestinationSelect,
  handlePackageMonthYear,
  handleOccupationChange,
  formInput,
  isDestinationEmpty,
  setIsDestinationEmpty,
  isOriginEmpty,
  setIsOriginEmpty,
  isDatesEmpty,
  isTuristasEmpty,
  setIsDatesEmpty,
  departureDate,
}) => {
  const maxPassengers = 3;

  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [adultsQuantity, setAdultsQuantity] = useState(formInput?.adultsQty || 1);
  const [totalTourists, setTotalTourists] = useState(formInput?.adultsQty || 1);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(formInput.origin ?? null);
  const [selectedDestination, setSelectedDestination] = useState(formInput.destination ?? null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [isMonthYearDialogOpen, setIsMonthYearDialogOpen] = useState(false);
  const [shouldPasajerosOpen, setShouldPasajerosOpen] = useState(false);
  const pasajerosRef = useRef(null);

  const [selectedMonth, setSelectedMonth] = useState(formInput?.selectedMonth || null);
  const [selectedYear, setSelectedYear] = useState(formInput?.selectedYear || null);

  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const prevFormInputRef = useRef(formInput);
  const autoSelectionRef = useRef(false);

  const originId = selectedOrigin?.id;
  const destinationId = selectedDestination?.id;
  const {
    availableDates,
    isLoading: isLoadingDates,
    error: datesError,
  } = useAvailableDates(originId, destinationId);

  const showError = useMemo(
    () => (message) => {
      setErrorMessage(message);
      setOpenToast(true);
    },
    [],
  );

  const handleCloseToast = useCallback(() => setOpenToast(false), []);

  const getUniqueItemsArray = useCallback((data) => {
    if (!data) return [];
    return Object.values(
      data.reduce((acc, item) => {
        const normalizedName = item.name.trim().toLowerCase();
        if (!acc[normalizedName]) {
          acc[normalizedName] = { name: item.name.trim(), id: [item.id] };
        } else {
          acc[normalizedName].id.push(item.id);
        }
        return acc;
      }, {}),
    );
  }, []);

  const [uniqueOriginsArray, uniqueDestinationsArray] = useMemo(
    () => [getUniqueItemsArray(origins?.data), getUniqueItemsArray(destinations?.data)],
    [origins?.data, destinations?.data, getUniqueItemsArray],
  );

  const handleAdultsQuantityChange = useCallback(
    (quantity) => {
      const newQuantity = Math.max(1, Math.min(quantity, maxPassengers));
      setAdultsQuantity(newQuantity);
      setTotalTourists(newQuantity);
      handleOccupationChange(newQuantity);

      if (newQuantity === maxPassengers) {
        showError(`El número máximo de adultos por paquete es ${maxPassengers}.`);
      }
    },
    [maxPassengers, showError, handleOccupationChange],
  );

  const handleTotalTouristsChange = useCallback(
    (total) => {
      const newTotal = Math.max(1, Math.min(total, maxPassengers));
      setTotalTourists(newTotal);
      setAdultsQuantity(newTotal);
      handleOccupationChange(newTotal);
    },
    [maxPassengers, handleOccupationChange],
  );

  const handleChangeOrigin = useCallback(
    (value) => {
      if (!value) {
        handleOriginSelect(null);
        setSelectedOrigin(null);
        handleDestinationSelect(null);
        setSelectedDestination(null);
        setSelectedMonth(null);
        setSelectedYear(null);
        autoSelectionRef.current = false;
        return;
      }

      if (!value.id || !Array.isArray(value.id) || value.id.length === 0) {
        console.warn('handleChangeOrigin: Invalid origin value', value);
        return;
      }

      if (value?.id?.[0] !== selectedOrigin?.id?.[0]) {
        handleOriginSelect(value);
        setSelectedOrigin(value);

        handleDestinationSelect(null);
        setSelectedDestination(null);
        setSelectedMonth(null);
        setSelectedYear(null);
        autoSelectionRef.current = false;

        if (originInputRef.current) {
          originInputRef.current.blur();
        }

        if (destinationInputRef.current && value) {
          setTimeout(() => {
            destinationInputRef.current.focus();
          }, 200);
        }

        if (value) {
          setIsDestinationOpen(true);
        }
      }
    },
    [handleOriginSelect, selectedOrigin, handleDestinationSelect],
  );

  const handleChangeDestination = useCallback(
    (value) => {
      if (!value) {
        handleDestinationSelect(null);
        setSelectedDestination(null);
        setSelectedMonth(null);
        setSelectedYear(null);
        autoSelectionRef.current = false;
        return;
      }

      if (!value.id || !Array.isArray(value.id) || value.id.length === 0) {
        console.warn('handleChangeDestination: Invalid destination value', value);
        return;
      }

      if (value?.id?.[0] !== selectedDestination?.id?.[0]) {
        handleDestinationSelect(value);
        setSelectedDestination(value);
        setSelectedMonth(null);
        setSelectedYear(null);
        autoSelectionRef.current = false;

        if (value) {
          setIsDestinationOpen(false);
        }
      }
    },
    [handleDestinationSelect, selectedDestination],
  );

  const handleMonthYearChange = useCallback(
    (month, year) => {
      setSelectedMonth(month);
      setSelectedYear(year);

      if (month && year) {
        handlePackageMonthYear(month, year);
        setIsDatesEmpty(false);
      } else {
        setIsDatesEmpty(true);
      }
    },
    [handlePackageMonthYear, setIsDatesEmpty],
  );

  useEffect(() => {
    if (selectedOrigin && selectedDestination) {
      if (selectedYear && selectedMonth) {
        setIsDatesEmpty(false);
      } else if (!availableDates || Object.keys(availableDates).length === 0) {
        setIsDatesEmpty(false);
      } else {
        setIsDatesEmpty(true);
      }
    } else {
      setIsDatesEmpty(false);
    }
  }, [
    selectedYear,
    selectedMonth,
    selectedOrigin,
    selectedDestination,
    setIsDatesEmpty,
    availableDates,
  ]);

  useEffect(() => {
    const prevInput = prevFormInputRef.current;

    if (formInput.origin?.id?.[0] !== prevInput.origin?.id?.[0]) {
      setSelectedOrigin(formInput.origin ?? null);
      if (formInput.origin !== prevInput.origin) {
        setSelectedDestination(null);
        setSelectedMonth(null);
        setSelectedYear(null);
      }
    }

    if (formInput.destination?.id?.[0] !== prevInput.destination?.id?.[0]) {
      setSelectedDestination(formInput.destination ?? null);
    }

    if (formInput.selectedMonth !== prevInput.selectedMonth) {
      setSelectedMonth(formInput.selectedMonth);
    }
    if (formInput.selectedYear !== prevInput.selectedYear) {
      setSelectedYear(formInput.selectedYear);
    }

    if (formInput.adultsQty !== prevInput.adultsQty) {
      const newQty = formInput.adultsQty > 0 ? formInput.adultsQty : 1;
      setAdultsQuantity(newQty);
      setTotalTourists(newQty);
    }

    prevFormInputRef.current = formInput;
  }, [
    formInput.origin?.id?.[0],
    formInput.destination?.id?.[0],
    formInput.selectedMonth,
    formInput.selectedYear,
    formInput.adultsQty,
  ]);

  useEffect(() => {
    if (
      departureDate &&
      (!selectedMonth ||
        !selectedYear ||
        !moment(moment(`${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`)).isSame(
          moment.utc(departureDate),
        ))
    ) {
      const departureMoment = moment.utc(departureDate);

      if (availableDates) {
        const year = departureMoment.year();
        const month = departureMoment.month() + 1;

        if (availableDates[year] && availableDates[year].includes(month)) {
          setSelectedYear(year);
          setSelectedMonth(month);
          setIsDatesEmpty(false);
        }
      }
    }
  }, [departureDate, availableDates, setIsDatesEmpty]);

  useEffect(() => {
    if (availableDates && Object.keys(availableDates).length > 0 && !selectedMonth && !selectedYear && !autoSelectionRef.current) {
      autoSelectionRef.current = true;
      
      const currentDate = moment();
      const currentYear = currentDate.year();
      const currentMonth = currentDate.month() + 1;
      
      const sortedYears = Object.keys(availableDates).sort((a, b) => parseInt(a) - parseInt(b));
      
      let bestYear = null;
      let bestMonth = null;
      
      for (const year of sortedYears) {
        const yearInt = parseInt(year);
        const months = availableDates[year].sort((a, b) => a - b);
        
        for (const month of months) {
          if (yearInt >= currentYear) {
            if (yearInt === currentYear) {
              if (month >= currentMonth) {
                bestYear = yearInt;
                bestMonth = month;
                break;
              }
            } else {
              bestYear = yearInt;
              bestMonth = month;
              break;
            }
          }
        }
        
        if (bestYear && bestMonth) break;
      }
      
      if (!bestYear || !bestMonth) {
        bestYear = parseInt(sortedYears[0]);
        bestMonth = availableDates[sortedYears[0]].sort((a, b) => a - b)[0];
      }
      
      if (bestYear && bestMonth) {
        setSelectedYear(bestYear);
        setSelectedMonth(bestMonth);
        handlePackageMonthYear(bestMonth, bestYear);
        setIsDatesEmpty(false);
      }
    }
  }, [availableDates]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(isClient ? window.innerWidth <= 768 : false);
    };

    checkMobile();
    isClient && window.addEventListener('resize', checkMobile);

    return () => {
      isClient && window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (!selectedOrigin || !selectedDestination) {
      if (pasajerosRef.current) {
        pasajerosRef.current.setAttribute('inert', '');
      }
      setShouldPasajerosOpen(false);
    } else if (pasajerosRef.current) {
      pasajerosRef.current.removeAttribute('inert');
    }
  }, [selectedOrigin, selectedDestination]);

  const handlePasajerosClose = useCallback(() => {
    if (pasajerosRef.current) {
      pasajerosRef.current.setAttribute('inert', '');
    }
    setShouldPasajerosOpen(false);
  }, []);

  const handlePasajerosOpen = useCallback(() => {
    if (selectedOrigin && selectedDestination) {
      if (pasajerosRef.current) {
        pasajerosRef.current.removeAttribute('inert');
      }
      setShouldPasajerosOpen(true);
    }
  }, [selectedOrigin, selectedDestination]);

  return (
    <>
      <Stack className="input-container">
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Desde dónde?</h3>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            onSelect={handleOriginSelect}
            options={uniqueOriginsArray}
            originSelected={formInput.origin}
            isOriginEmpty={isOriginEmpty}
            setIsOriginEmpty={setIsOriginEmpty}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            isLoading={origins?.isLoading}
          />
        ) : (
          <Autocomplete
            id="origin-autocomplete"
            className={`form__field origin-typeahead ${isOriginEmpty ? 'autocomplete-error' : ''}`}
            noOptionsText="No hay resultados"
            size="small"
            onChange={(_, value) => handleChangeOrigin(value)}
            value={selectedOrigin}
            defaultValue={formInput.origin}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            onFocus={() => {
              const originInput = document.querySelector('.origin-typeahead');
              if (originInput) {
                const closeBtn = originInput.querySelector('.MuiAutocomplete-clearIndicator');
                if (closeBtn) {
                  closeBtn.click();
                }
              }
            }}
            options={uniqueOriginsArray}
            loading={origins?.isLoading}
            style={{ width: '100%' }}
            loadingText="Cargando orígenes"
            getOptionLabel={(option) => option.name}
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(input) => (originInputRef.current = input)}
                className="focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent"
                error={isOriginEmpty}
                helperText={isOriginEmpty ? 'Ingresa tu origen' : ''}
              />
            )}
          />
        )}
      </Stack>

      <Stack className="input-container">
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Hasta dónde?</h3>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            onSelect={handleChangeDestination}
            options={uniqueDestinationsArray}
            originSelected={formInput.destination}
            disabled={!formInput?.origin}
            isDestinationEmpty={isDestinationEmpty}
            setIsDestinationEmpty={setIsDestinationEmpty}
            isOriginEmpty={isOriginEmpty}
            setIsOriginEmpty={setIsOriginEmpty}
            isDestination
            isOpen={isDestinationDialogOpen}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsFullScreenRangeDatePickerOpen={setIsMonthYearDialogOpen}
            isLoading={destinations?.isLoading}
          />
        ) : (
          <Autocomplete
            id="destination-autocomplete"
            className={`form__field destination-typeahead ${
              isDestinationEmpty ? 'autocomplete-error' : ''
            }`}
            noOptionsText="No hay resultados"
            loadingText="Cargando destinos"
            loading={destinations?.isLoading}
            onFocus={() => {
              setIsDestinationEmpty(false);
              setIsDestinationOpen(true);
            }}
            style={{ width: '100%' }}
            onChange={(_, value) => handleChangeDestination(value)}
            onBlur={() => setIsDestinationOpen(false)}
            size="small"
            defaultValue={formInput.destination}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            open={isDestinationOpen}
            disabled={!formInput?.origin}
            options={uniqueDestinationsArray}
            getOptionLabel={(option) => option.name}
            value={selectedDestination}
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                className="focus:ring-transparent"
                inputRef={(input) => (destinationInputRef.current = input)}
                error={isDestinationEmpty}
                helperText={isDestinationEmpty ? 'Ingresa tu destino' : ''}
                onFocus={() => {
                  const input = document.querySelector('.destination-typeahead');
                  if (input) {
                    const closeBtn = input.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                }}
              />
            )}
          />
        )}
      </Stack>

      <Stack
        className={`form__field date-picker ${
          isDatesEmpty &&
          !isLoadingDates &&
          availableDates &&
          Object.keys(availableDates).length > 0
            ? 'dates-error'
            : ''
        }`}
      >
        {isMobile ? (
          <MonthYearGroupedFullScreenDialog
            isOpen={isMonthYearDialogOpen}
            onClose={() => setIsMonthYearDialogOpen(false)}
            availableDates={availableDates}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthYearChange={handleMonthYearChange}
            onNext={handlePasajerosOpen}
            disabled={!selectedOrigin || !selectedDestination || !availableDates || Object.keys(availableDates).length === 0}
            isLoading={isLoadingDates}
            error={datesError}
          />
        ) : (
          <MonthYearGroupedSelector
            availableDates={availableDates}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthYearChange={handleMonthYearChange}
            disabled={!selectedOrigin || !selectedDestination || !availableDates || Object.keys(availableDates).length === 0}
            isLoading={isLoadingDates}
            error={datesError}
          />
        )}
        {datesError && (
          <Typography variant="caption" color="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
            {datesError}
          </Typography>
        )}
      </Stack>

      <Stack
        className={`pasajeros-input-container ${
          !selectedOrigin || !selectedDestination ? 'disabled' : ''
        }`}
        ref={pasajerosRef}
        sx={{
          opacity: !selectedOrigin || !selectedDestination ? 0.5 : 1,
          pointerEvents: !selectedOrigin || !selectedDestination ? 'none' : 'auto',
          cursor: !selectedOrigin || !selectedDestination ? 'not-allowed' : 'pointer',
        }}
      >
        <Typography fontSize={12} color={'#4f4f4f'}>
          ¿Cuántos viajan?
        </Typography>
        <Stack>
          {isMobile ? (
            <PasajerosFullScreenDialog
              isPackage
              totalTourists={totalTourists}
              setTotalTourists={handleTotalTouristsChange}
              adultsQuantity={adultsQuantity}
              setAdultsQuantity={handleAdultsQuantityChange}
              shouldOpen={shouldPasajerosOpen && selectedOrigin && selectedDestination}
              disabled={!selectedOrigin || !selectedDestination}
              onOpen={handlePasajerosOpen}
              onClose={handlePasajerosClose}
            />
          ) : (
            <PasajerosSelector
              isPackage
              adultsQuantity={adultsQuantity}
              isTuristasEmpty={isTuristasEmpty}
              setAdultsQuantity={handleAdultsQuantityChange}
              totalTourists={totalTourists}
              disabled={!selectedOrigin || !selectedDestination}
              onClick={handlePasajerosOpen}
              onClose={handlePasajerosClose}
            />
          )}
        </Stack>
        <ErrorSnackbar
          open={openToast}
          message={errorMessage}
          onClose={handleCloseToast}
          severity="info"
        />
      </Stack>
    </>
  );
};

export default PackageFilterForm;
